import { ColumnSort } from '@tanstack/react-table';
import { IFormSection, IManifestField } from '../manifests/types';
import { IUserRole } from './userRoles';

export { IUserRole };

export interface UserLifecycleFlag {
    createdAt: string;
    updatedAt: string;
    val: boolean;
}
export interface IUser {
    clientId: string;
    contactId: string;
    email: string;
    userId?: string;
    firebaseId: string;
    firebaseEmail: string;
    hasSignedAgreement: boolean;
    preferredLang?: string;
    disabled?: boolean;
    lifecycleFlags?: Record<string, UserLifecycleFlag> & { _id: string };
    name: string;
    _id: string;
    roles?: IUserRole[];
    organization?:
        | string
        | {
              id: string;
              _id: string;
              name: string;
          };
}

export interface IUserDocument<ContactType = string, OrgType = string> {
    contact?: ContactType;
    createdAt: string;
    updatedAt: string;
    disabled: boolean;
    firebaseEmail?: string;
    firebaseId?: string;
    preferredLang?: string;
    userId?: string;
    name?: string;
    organization?:
        | OrgType
        | {
              id: string;
              _id: string;
              name: string;
          };
    lifecycleFlags?: Record<string, UserLifecycleFlag> & { _id: string };
    roles?: IUserRole[];
    id: string;
    _id: string;
}

export interface IUserDocumentPopulatedContact {
    _id: string;
    name: { first: string; last: string; fullName: string };
}
export interface IUserDocumentPopulatedOrganization {
    _id: string;
    name: string;
}
export type IUserDocumentPopulated = IUserDocument<IUserDocumentPopulatedContact, IUserDocumentPopulatedOrganization>;

export enum UserTypes {
    ADMIN = 'admin',
    AUTHENTICATED = 'authenticated'
}

export enum Permissions {
    READ = 'readonly',
    WRITE = 'write'
}

export type IContact = {
    id?: string;
    firstName?: string;
    middleInitial?: string;
    lastName?: string;
    name?: string;
    email?: string;
    secondaryEmail?: string;
    phone?: string | number;
    homePhone?: string | number;
    mobilePhone?: string | number;
    gender?: string;
    race?: string;
    clientRelationshipManager?: string;
    portfolContactId?: string;
    okToContact?: boolean;
    disabled?: boolean;
    veteran?: boolean;
};

interface IContactName {
    pronouns: string;
    first: string;
    middleInitial: string;
    last: string;
    suffix: string;
    honorific: string;
    createdAt: string;
    updatedAt: string;
    fullName?: string;
}

export interface IContactMini {
    id: string;
    name: IContactName;
}

export interface IPhoneNumber {
    areaCode?: string;
    number?: string;
    ext?: string;
}

export interface ISalaryRangeMetaItem {
    high?: number;
    low?: number;
}
export interface ISalaryRangeMeta {
    hour: ISalaryRangeMetaItem;
    annual?: ISalaryRangeMetaItem;
}

export interface ISalaryRange {
    total?: number;
    ft?: number;
    pt?: number;
    meta: ISalaryRangeMeta;
}

export interface IDemographics {
    gender: string;
    raceEthnicity: string[];
    isVeteran?: boolean;
    isDisabled?: boolean;
    createdAt: string;
    updatedAt: string;
}

// Temp
export type IOrganizationContactRecord = {
    contact: string;
    percentOwnership: number;
    _id: string;
    createdAt: string;
    updatedAt: string;
    id: string;
};

export type IContactRecord = {
    email: {
        primary: string;
        secondary?: string;
    };
    phone: {
        primary: IPhoneNumber;
        home: IPhoneNumber;
        mobile: IPhoneNumber;
    };
    _id: string;
    createdAt: string;
    demographics: IDemographics;
    isOkayToContact: boolean;
    name: IContactName;
    organization: {
        _id: string;
        name: string;
    };
    portfolId: string;
    updatedAt: string;
    id: string;
    jobTitle?: string;
    percentOwnership?: number;
};

export type ISurveyRecord = {
    _id: string;
    createdAt: string;
    updatedAt: string;
    id: string;
};

export interface ITargetPeriod {
    year: number;
    month: number;
    _id: string;
}
export interface ISurveyTargetPeriod {
    start: ITargetPeriod;
    end: ITargetPeriod;
}

export enum SurveyStatus {
    IN_PROGRESS = 'In Progress',
    PENDING = 'Pending',
    SUBMITTED = 'Submitted',
    UPCOMING = 'Upcoming',
    ACCEPTED = 'Accepted',
    REJECTED = 'Rejected',
    COMPLETED = 'Complete'
}

export enum OnboardingStatus {
    IN_PROGRESS = 'In Progress',
    PENDING = 'Pending',
    COMPLETED = 'Complete'
}

export type ISurveyNoteType = 'APPROVE' | 'REJECT' | 'NOTE_INTERNAL' | 'NOTE_EXTERNAL';
export type ISurveyNoteDataRelation = {
    data: any;
    type: string;
    _id: string;
};

export interface ISurveyNote {
    createdAt: string;
    note: string;
    type: ISurveyNoteType;
    updatedAt: string;
    user: {
        _id: string;
        id: string;
        name: string;
    };
    _id: string;
    dataRelation?: ISurveyNoteDataRelation;
}

export type ISurveyFull = {
    _id: string;
    createdAt: string;
    updatedAt: string;
    dateSent?: string;
    reminderLastSent?: string;
    id: string;
    completedMessage?: string;
    description?: string;
    name?: string;
    organization?:
        | string
        | {
              id: string;
              _id: string;
              name: string;
          };
    status: SurveyStatus;
    trigger: string;
    loanGroup?: {
        name: string;
        _id: string;
    };
    data: {
        ancillary?: Record<string, any>;
        keyed?: Record<string, any>;
        stepCompletion: number;
        createdAt: string;
    };
    manifest: {
        sections: IFormSection[];
        _id: string;
    };
    targetPeriod: ISurveyTargetPeriod;
    notes?: ISurveyNote[];
};

export type ISurveyInvite = {
    createdAt: string;
    firstSent: string;
    lastSent?: string;
    id: string;
    updatedAt: string;
    _id: string;
};

export type IContactRow = {
    id: string;
    portfolId: string;
    name: string;
    phone: string;
    email: string;
    isOkayToContact: boolean;
    gender: string;
    race: string[];
    organization: {
        id: string;
        name: string;
    };
    subRows?: IContactRow[];
};

export type IChildOrgRecord = {
    _id: string;
    createdAt: string;
    id: string;
    name: string;
    portfolId: string;
    updatedAt: string;
    inheritedLoans: string[];
};

export type IOrgRecord = {
    _id: string;
    createdAt: string;
    daysAfterFiscalYearEndThatReportIsDue: number;
    dba: string;
    formOfOrganization: string;
    ein: string;
    id: string;
    isNonProfit: boolean;
    isOkayToContact: boolean;
    hasActiveLoan: boolean;
    monthFiscalYearEnds: string;
    name: string;
    portfolId: string;
    primaryContact: IContactMini;
    relationshipManager: string;
    typeOfBusiness: string;
    updatedAt: string;
    yearEstablished: string;
    contacts: IContactRecord[];
    childOrganizations?: IChildOrgRecord[];
    onboardingStatus: OnboardingStatus;
    status: string;
};

export type IOrganization = {
    id: string;
    ein: string;
    portfolId: string;
    name: string;
    dba: string;
    typeOfBusiness: string;
    yearEstablished: string;
    formOfOrganization: string;
    isNonProfit: boolean;
    isOkayToContact: boolean;
    hasActiveLoan: boolean;
    relationshipManager: string;
    monthFiscalYearEnds: string;
    daysAfterFiscalYearEndThatReportIsDue: number;
    primaryContact: IContactMini;
    subRows?: IOrganization[];
    childOrganizations?: IChildOrgRecord[];
};
export interface IBalanceHistoryItemRecord {
    asOf: string;
    balance: number;
    createdAt: string;
    isInDefault: boolean;
    updatedAt: string;
}

export interface IBalanceHistoryItem {
    balance: number;
    isInDefault: boolean;
    asOf?: Date;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ILoanGroupRecord {
    createdAt: string;
    id: string;
    name: string;
    loans: string[];
    enrolled: boolean;
    originDate: string;
    groupFinancingTotal?: string;
    surveys?: {
        intake?: ISurveyFull | null;
        biannual?: ISurveyFull[] | null;
        other?: ISurveyFull[] | null;
    };
    organization: {
        _id: string;
        name: string;
    };
    updatedAt: string;
    _id: string;
}
export interface ILoanRecord {
    actualSettlementDate: string;
    approvedLoanAmount: number;
    balanceHistory: IBalanceHistoryItemRecord[];
    currentStatus: IBalanceHistoryItemRecord;
    createdAt: string;
    effectiveDateOfTermination: string;
    id: string;
    isActive: boolean;
    isClosed: boolean;
    loanNumber: string;
    loanOfficer: string;
    loanCorp: string;
    organization: {
        _id: string;
        name: string;
    };
    updatedAt: string;
    _id: string;
}

export type ILoan = {
    id: string;
    approvedLoanAmount: number;
    isActive: boolean;
    isClosed: boolean;
    loanNumber: string;
    loanOfficer: string;
    loanCorp: string;
    organization: {
        id: string;
        name: string;
    };
    createdAt?: Date;
    effectiveDateOfTermination?: Date;
    updatedAt?: Date;
    actualSettlementDate?: Date;
    balanceHistory: IBalanceHistoryItem[];
    currentStatus?: IBalanceHistoryItem;
    subRows?: ILoan[];
};

export type WithCompleteness<T extends {}> = T & {
    completeness?: number;
};

export interface TableQueryParams {
    pageData: {
        index: number;
        size: number;
    };
    sort?: ColumnSort;
    filter?: Record<string, string[]>;
    search?: string;
}

export enum ROW_TYPES {
    SECTION_HEADER = 'section-header',
    TOTAL = 'total',
    SUM_OF = 'sum-of',
    AMOUNT = 'amount',
    READ_ONLY = 'react-only'
}
export interface SummationLineItem {
    label: string | number;
    value?: number;
    key: string;
    id: string;
    required?: boolean;
    hidden?: boolean;
    type: ROW_TYPES;
    sumOf?: string[];
    displayAsNegative?: boolean;
    isTotal?: boolean;
    fieldConfig?: IManifestField;
}

export interface ICostTableValue {
    total?: number;
    items: SummationLineItem[];
}

export interface ISurveyBatch {
    surveyTrigger: string;
    targetSurveys?: string[];
    targetWindow?: number;
    targetYear?: number;
    clientId?: string;
}
export interface ISurveyFinancial {
    targetYear?: number;
    clientId?: string;
    trigger?: string;
}

export interface IExistingSurvey {
    _id: string;
    name: string;
    organization: {
        _id: string;
        name: string;
    };
    reason?: string;
}

export interface IOmittedFromBatch {
    error?: IExistingSurvey[];
    existingSurvey?: IExistingSurvey[];
}

export interface BatchResponse {
    surveysGenerated: any[];
    omittedLoanGroups?: IOmittedFromBatch;
    omittedOrganizations?: IOmittedFromBatch;
}

export type IManifest = Record<string, any>;
